/**
 * Analytics ML API Service - интеграция с backend /analytics endpoints
 * Event Master v1.5 - Backend Integration
 */

import { httpClient } from "../http-client.service";
import { ApiResponse } from "../../types/shared-types";
import {
  IAnalyticsDashboard,
  IAnalyticsWidget,
  IMLModel,
  IDateRange,
  AnalyticsWidgetType,
  MLModelType,
  DataSourceType,
} from "../../types/analytics";

import type { AnalyticsMlGetMetricsData } from "../../api/generated/types.gen";

import {
  ANALYTICS_BASE,
  ANALYTICS_ML_BASE,
} from "../../constants/api-endpoints";

// W2-S3 (ANL-003): ML-CRUD (dashboards/widgets/ml-models/data-sources/reports/
// metrics/predictions/system) обслуживает AnalyticsMLController на /analytics/ml.
// insights / analytics-events / events (tracking) — AnalyticsController на /analytics.
const BASE_URL = ANALYTICS_ML_BASE;
const CORE_BASE_URL = ANALYTICS_BASE;

// ===== DASHBOARDS =====

export interface DashboardQuery {
  userId?: string;
  isPublic?: boolean;
}

export const getDashboards = async (
  params?: DashboardQuery,
): Promise<IAnalyticsDashboard[]> => {
  const { data } = await httpClient.get<ApiResponse<IAnalyticsDashboard[]>>(
    `${BASE_URL}/dashboards`,
    { params },
  );
  return data.data;
};

export const createDashboard = async (
  dashboard: Partial<IAnalyticsDashboard>,
): Promise<IAnalyticsDashboard> => {
  const { data } = await httpClient.post<ApiResponse<IAnalyticsDashboard>>(
    `${BASE_URL}/dashboards`,
    dashboard,
  );
  return data.data;
};

export const getDashboard = async (
  id: string,
): Promise<IAnalyticsDashboard> => {
  const { data } = await httpClient.get<ApiResponse<IAnalyticsDashboard>>(
    `${BASE_URL}/dashboards/${id}`,
  );
  return data.data;
};

export const updateDashboard = async (
  id: string,
  dashboard: Partial<IAnalyticsDashboard>,
): Promise<IAnalyticsDashboard> => {
  const { data } = await httpClient.put<ApiResponse<IAnalyticsDashboard>>(
    `${BASE_URL}/dashboards/${id}`,
    dashboard,
  );
  return data.data;
};

export const deleteDashboard = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/dashboards/${id}`);
};

// ===== WIDGETS =====

export interface WidgetQuery {
  dashboardId?: string;
  type?: AnalyticsWidgetType;
}

export const getWidgets = async (
  params?: WidgetQuery,
): Promise<IAnalyticsWidget[]> => {
  const { data } = await httpClient.get<ApiResponse<IAnalyticsWidget[]>>(
    `${BASE_URL}/widgets`,
    { params },
  );
  return data.data;
};

export const createWidget = async (
  widget: Partial<IAnalyticsWidget>,
): Promise<IAnalyticsWidget> => {
  const { data } = await httpClient.post<ApiResponse<IAnalyticsWidget>>(
    `${BASE_URL}/widgets`,
    widget,
  );
  return data.data;
};

export const getWidget = async (id: string): Promise<IAnalyticsWidget> => {
  const { data } = await httpClient.get<ApiResponse<IAnalyticsWidget>>(
    `${BASE_URL}/widgets/${id}`,
  );
  return data.data;
};

export const updateWidget = async (
  id: string,
  widget: Partial<IAnalyticsWidget>,
): Promise<IAnalyticsWidget> => {
  const { data } = await httpClient.put<ApiResponse<IAnalyticsWidget>>(
    `${BASE_URL}/widgets/${id}`,
    widget,
  );
  return data.data;
};

export const deleteWidget = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/widgets/${id}`);
};

// ===== ML MODELS =====

export interface MLModelQuery {
  type?: MLModelType;
  status?: string;
}

export const getMLModels = async (
  params?: MLModelQuery,
): Promise<IMLModel[]> => {
  const { data } = await httpClient.get<ApiResponse<IMLModel[]>>(
    `${BASE_URL}/ml-models`,
    { params },
  );
  return data.data;
};

export const createMLModel = async (
  model: Partial<IMLModel>,
): Promise<IMLModel> => {
  const { data } = await httpClient.post<ApiResponse<IMLModel>>(
    `${BASE_URL}/ml-models`,
    model,
  );
  return data.data;
};

export const getMLModel = async (id: string): Promise<IMLModel> => {
  const { data } = await httpClient.get<ApiResponse<IMLModel>>(
    `${BASE_URL}/ml-models/${id}`,
  );
  return data.data;
};

export const updateMLModel = async (
  id: string,
  model: Partial<IMLModel>,
): Promise<IMLModel> => {
  const { data } = await httpClient.put<ApiResponse<IMLModel>>(
    `${BASE_URL}/ml-models/${id}`,
    model,
  );
  return data.data;
};

export const deleteMLModel = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/ml-models/${id}`);
};

export const trainMLModel = async (
  id: string,
): Promise<{ status: string; jobId: string }> => {
  const { data } = await httpClient.post<
    ApiResponse<{ status: string; jobId: string }>
  >(`${BASE_URL}/ml-models/${id}/train`);
  return data.data;
};

export const deployMLModel = async (
  id: string,
): Promise<{ status: string }> => {
  const { data } = await httpClient.post<ApiResponse<{ status: string }>>(
    `${BASE_URL}/ml-models/${id}/deploy`,
  );
  return data.data;
};

// ===== DATA SOURCES =====

/** Managed data source entity returned by the backend CRUD API. */
export interface DataSourceRecord {
  id: string;
  name: string;
  type: DataSourceType;
  config: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDataSourceDto {
  name: string;
  type: DataSourceType;
  config?: Record<string, unknown>;
}

export interface UpdateDataSourceDto {
  name?: string;
  type?: DataSourceType;
  config?: Record<string, unknown>;
}

export interface DataSourceQuery {
  type?: DataSourceType;
}

export const getDataSources = async (
  params?: DataSourceQuery,
): Promise<DataSourceRecord[]> => {
  const { data } = await httpClient.get<ApiResponse<DataSourceRecord[]>>(
    `${BASE_URL}/data-sources`,
    { params },
  );
  return data.data;
};

export const createDataSource = async (
  dto: CreateDataSourceDto,
): Promise<DataSourceRecord> => {
  const { data } = await httpClient.post<ApiResponse<DataSourceRecord>>(
    `${BASE_URL}/data-sources`,
    dto,
  );
  return data.data;
};

export const getDataSource = async (id: string): Promise<DataSourceRecord> => {
  const { data } = await httpClient.get<ApiResponse<DataSourceRecord>>(
    `${BASE_URL}/data-sources/${id}`,
  );
  return data.data;
};

export const updateDataSource = async (
  id: string,
  dto: UpdateDataSourceDto,
): Promise<DataSourceRecord> => {
  const { data } = await httpClient.put<ApiResponse<DataSourceRecord>>(
    `${BASE_URL}/data-sources/${id}`,
    dto,
  );
  return data.data;
};

export const deleteDataSource = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/data-sources/${id}`);
};

// ===== REPORTS =====

export interface ReportQuery {
  userId?: string;
  type?: string;
}

export interface AnalyticsReport {
  id: string;
  name: string;
  type: string;
  description?: string;
  config: any;
  data: any;
  createdAt: Date;
  updatedAt: Date;
}

export const getReports = async (
  params?: ReportQuery,
): Promise<AnalyticsReport[]> => {
  const { data } = await httpClient.get<ApiResponse<AnalyticsReport[]>>(
    `${BASE_URL}/reports`,
    { params },
  );
  return data.data;
};

export const createReport = async (
  report: Partial<AnalyticsReport>,
): Promise<AnalyticsReport> => {
  const { data } = await httpClient.post<ApiResponse<AnalyticsReport>>(
    `${BASE_URL}/reports`,
    report,
  );
  return data.data;
};

export const getReport = async (id: string): Promise<AnalyticsReport> => {
  const { data } = await httpClient.get<ApiResponse<AnalyticsReport>>(
    `${BASE_URL}/reports/${id}`,
  );
  return data.data;
};

export const updateReport = async (
  id: string,
  report: Partial<AnalyticsReport>,
): Promise<AnalyticsReport> => {
  const { data } = await httpClient.put<ApiResponse<AnalyticsReport>>(
    `${BASE_URL}/reports/${id}`,
    report,
  );
  return data.data;
};

export const deleteReport = async (id: string): Promise<void> => {
  await httpClient.delete(`${BASE_URL}/reports/${id}`);
};

// ===== METRICS =====

// AD-134: параметры берём с типа контракта. Прежний `extends IDateRange` объявлял
// обязательные `end` и `timezone`, которых у маршрута нет: с `forbidNonWhitelisted`
// такой запрос получил бы 400.
export type MetricsQuery = NonNullable<AnalyticsMlGetMetricsData["query"]>;

export interface AnalyticsMetrics {
  period: IDateRange;
  data: Record<string, any>;
  charts: any[];
  summary: {
    totalEvents: number;
    totalUsers: number;
    totalRevenue: number;
    growth: Record<string, number>;
  };
}

export const getMetrics = async (
  params: MetricsQuery,
): Promise<AnalyticsMetrics> => {
  const { data } = await httpClient.get<ApiResponse<AnalyticsMetrics>>(
    `${BASE_URL}/metrics`,
    { params },
  );
  return data.data;
};

export interface RealtimeMetrics {
  metrics: Array<{ name: string; value: number }>;
  timestamp: string;
}

export const getRealTimeMetrics = async (): Promise<RealtimeMetrics> => {
  const { data } = await httpClient.get<ApiResponse<RealtimeMetrics>>(
    `${BASE_URL}/metrics/real-time`,
  );
  return data.data;
};

// ===== PREDICTIONS =====

export interface PredictionQuery {
  modelId?: string;
  limit?: number;
}

export interface PredictionRequest {
  modelId: string;
  input: Record<string, any>;
  context?: Record<string, any>;
}

export interface PredictionItem {
  id: string;
  prediction: string;
  confidence: number;
  estimatedParticipants?: number;
  factors: string[];
}

export interface PredictionResult {
  prediction: Record<string, unknown>;
  confidence: number;
}

export const getPredictions = async (
  params?: PredictionQuery,
): Promise<PredictionItem[]> => {
  const { data } = await httpClient.get<ApiResponse<PredictionItem[]>>(
    `${BASE_URL}/predictions`,
    { params },
  );
  return data.data;
};

export const generatePrediction = async (
  request: PredictionRequest,
): Promise<PredictionResult> => {
  const { data } = await httpClient.post<ApiResponse<PredictionResult>>(
    `${BASE_URL}/predictions`,
    request,
  );
  return data.data;
};

// ===== SYSTEM =====

export interface SystemHealth {
  status: "healthy" | "degraded" | "unhealthy";
  timestamp: string;
  services: {
    database: "up" | "down";
    ml: "up" | "down";
    analytics: "up" | "down";
  };
  metrics: {
    responseTime: number;
    throughput: number;
    errorRate: number;
  };
}

export interface SystemStats {
  dashboards: number;
  widgets: number;
  mlModels: number;
  dataSources: number;
  reports: number;
  predictions: number;
  uptime: number;
}

export const getSystemHealth = async (): Promise<SystemHealth> => {
  const { data } = await httpClient.get<ApiResponse<SystemHealth>>(
    `${BASE_URL}/system/health`,
  );
  return data.data;
};

export const getSystemStats = async (): Promise<SystemStats> => {
  const { data } = await httpClient.get<ApiResponse<SystemStats>>(
    `${BASE_URL}/system/stats`,
  );
  return data.data;
};

// ===== AI INSIGHTS =====

export interface AiInsight {
  id: string;
  type: string;
  title: string;
  summary: string;
  recommendation: string | null;
  severity: "low" | "medium" | "high" | "critical";
  generatedAt: string;
  metadata: Record<string, unknown> | null;
}

export interface AiInsightsResponse {
  insights: AiInsight[];
  recommendations: string[];
  anomalies: AiInsight[];
}

export const getInsights = async (): Promise<AiInsightsResponse> => {
  const { data } = await httpClient.get<ApiResponse<AiInsightsResponse>>(
    `${CORE_BASE_URL}/insights`,
  );
  return data.data;
};

// ===== ANALYTICS EVENTS LOG =====

export enum AnalyticsEventType {
  USER_LOGIN = "user_login",
  USER_LOGOUT = "user_logout",
  USER_REGISTRATION = "user_registration",
  USER_PROFILE_UPDATE = "user_profile_update",
  EVENT_CREATED = "event_created",
  EVENT_UPDATED = "event_updated",
  EVENT_DELETED = "event_deleted",
  EVENT_PARTICIPANT_JOINED = "event_participant_joined",
  EVENT_PARTICIPANT_LEFT = "event_participant_left",
  TASK_CREATED = "task_created",
  TASK_UPDATED = "task_updated",
  TASK_DELETED = "task_deleted",
  TASK_STATUS_CHANGED = "task_status_changed",
  TASK_ASSIGNEE_CHANGED = "task_assignee_changed",
  TASK_COMMENT_ADDED = "task_comment_added",
  FILE_UPLOADED = "file_uploaded",
  FILE_DOWNLOADED = "file_downloaded",
  NOTIFICATION_SENT = "notification_sent",
  NOTIFICATION_READ = "notification_read",
  API_ERROR = "api_error",
  SEARCH_PERFORMED = "search_performed",
  PAGE_VIEW = "page_view",
}

export interface AnalyticsEventEntry {
  id: string;
  eventType: AnalyticsEventType;
  userId?: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  path?: string;
  createdAt: string;
}

export interface AnalyticsEventsLogFilters {
  eventTypes?: AnalyticsEventType[];
  userId?: string;
  entityType?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  path?: string;
  limit?: number;
  offset?: number;
}

export interface AnalyticsEventsLogResponse {
  events: AnalyticsEventEntry[];
  total: number;
}

export const getAnalyticsEventsLog = async (
  filters: AnalyticsEventsLogFilters = {},
): Promise<AnalyticsEventsLogResponse> => {
  const { data } = await httpClient.get<
    ApiResponse<AnalyticsEventsLogResponse>
  >(`${CORE_BASE_URL}/analytics-events`, { params: filters });
  return data.data;
};

// ===== EVENT TRACKING =====

export interface TrackEventRequest {
  eventType: AnalyticsEventType;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  path?: string;
  sessionId?: string;
}

export const trackAnalyticsEvent = async (
  request: TrackEventRequest,
): Promise<{ id: string }> => {
  const { data } = await httpClient.post<ApiResponse<{ id: string }>>(
    `${CORE_BASE_URL}/events`,
    request,
  );
  return data.data;
};

// ===== EXPORT =====

const analyticsMlApi = {
  // Dashboards
  getDashboards,
  createDashboard,
  getDashboard,
  updateDashboard,
  deleteDashboard,

  // Widgets
  getWidgets,
  createWidget,
  getWidget,
  updateWidget,
  deleteWidget,

  // ML Models
  getMLModels,
  createMLModel,
  getMLModel,
  updateMLModel,
  deleteMLModel,
  trainMLModel,
  deployMLModel,

  // Data Sources
  getDataSources,
  createDataSource,
  getDataSource,
  updateDataSource,
  deleteDataSource,

  // Reports
  getReports,
  createReport,
  getReport,
  updateReport,
  deleteReport,

  // Metrics
  getMetrics,
  getRealTimeMetrics,

  // Predictions
  getPredictions,
  generatePrediction,

  // System
  getSystemHealth,
  getSystemStats,

  // Insights
  getInsights,

  // Events Log
  getAnalyticsEventsLog,

  // Tracking
  trackAnalyticsEvent,
};
export default analyticsMlApi;
